class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keydown', (evt) => {
      if (evt.code === 'Escape') this.escapeFromItemDialog = Boolean(this.querySelector('dialog[open]'));
    });
    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this.escapeFromItemDialog) {
        this.escapeFromItemDialog = false;
        return;
      }
      this.close();
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!window.vinciCartItemDialogsInstalled) {
  window.vinciCartItemDialogsInstalled = true;

  const parseVariants = (dialog) => {
    if (dialog.variantData) return dialog.variantData;
    const source = dialog.querySelector('[data-cart-variants]');
    dialog.variantData = source ? JSON.parse(source.textContent) : [];
    return dialog.variantData;
  };

  const selectedOptions = (dialog) =>
    [...dialog.querySelectorAll('[data-cart-option-group]')].map(
      (group) => group.querySelector('[data-cart-option-value][aria-pressed="true"]')?.dataset.cartOptionValue
    );

  const selectedVariant = (dialog) => {
    const options = selectedOptions(dialog);
    if (!options.length) {
      return parseVariants(dialog).find((variant) => Number(variant.id) === Number(dialog.dataset.currentVariantId));
    }
    return parseVariants(dialog).find(
      (variant) =>
        variant.options.length === options.length &&
        variant.options.every((option, index) => option === options[index])
    );
  };

  const formatMoney = (dialog, cents) => {
    const currency = dialog.dataset.currency || window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  };

  const refreshEditDialog = (dialog) => {
    const variant = selectedVariant(dialog);
    const confirmButton = dialog.querySelector('[data-cart-edit-confirm]');
    const price = dialog.querySelector('[data-cart-dialog-price]');
    const image = dialog.querySelector('[data-cart-dialog-image]');

    if (!variant || !variant.available) {
      confirmButton.disabled = true;
      confirmButton.textContent = dialog.dataset.unavailableLabel;
      return null;
    }

    confirmButton.disabled = false;
    confirmButton.textContent = dialog.dataset.updateLabel;
    price.textContent = formatMoney(dialog, variant.price);
    if (image && variant.image) {
      image.src = variant.image;
      image.alt = variant.image_alt || '';
    }
    return variant;
  };

  const setBusy = (dialog, busy, buttonSelector, busyLabel, defaultLabel) => {
    const confirmButton = dialog.querySelector(buttonSelector);
    dialog.querySelectorAll('button, input').forEach((control) => {
      control.disabled = busy;
    });
    confirmButton.textContent = busy ? busyLabel : defaultLabel;
  };

  const cartSectionsPayload = () => ({
    sections: ['cart-drawer', 'cart-icon-bubble'],
    sections_url: window.location.pathname,
  });

  const requestCartUpdate = async (dialog, url, payload) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...payload, ...cartSectionsPayload() }),
    });

    let state;
    try {
      state = await response.json();
    } catch (parseError) {
      throw new Error(dialog.dataset.errorMessage);
    }

    if (!response.ok || state.errors || state.status) {
      throw new Error(dialog.dataset.errorMessage);
    }

    return state;
  };

  const renderCartState = (dialog, state) => {
    const drawer = document.querySelector('cart-drawer');
    const drawerContainer = document.getElementById('CartDrawer');
    const drawerMarkup = state.sections?.['cart-drawer'];

    if (!drawer || !drawerContainer || !drawerMarkup) {
      throw new Error(dialog.dataset.errorMessage);
    }

    const drawerDocument = new DOMParser().parseFromString(drawerMarkup, 'text/html');
    const incomingDrawer = drawerDocument.getElementById('CartDrawer');
    if (!incomingDrawer) throw new Error(dialog.dataset.errorMessage);

    dialog.close();
    drawerContainer.innerHTML = incomingDrawer.innerHTML;
    drawer.classList.toggle('is-empty', Number(state.item_count) === 0);

    const iconMarkup = state.sections?.['cart-icon-bubble'];
    const currentIcon = document.getElementById('cart-icon-bubble');
    if (iconMarkup && currentIcon) {
      const iconDocument = new DOMParser().parseFromString(iconMarkup, 'text/html');
      const incomingIcon = iconDocument.getElementById('cart-icon-bubble') || iconDocument.querySelector('.shopify-section');
      if (incomingIcon) currentIcon.innerHTML = incomingIcon.innerHTML;
    }

    drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
    removeTrapFocus();
    drawer.open();
    const focusContainer = Number(state.item_count) === 0
      ? drawer.querySelector('.drawer__inner-empty')
      : drawerContainer;
    trapFocus(focusContainer, drawer.querySelector('.drawer__close'));
    document.querySelector('cart-items')?.onCartUpdate();
  };

  const changeLineQuantity = async (dialog, quantity) => {
    const state = await requestCartUpdate(dialog, dialog.dataset.cartChangeUrl, {
      id: dialog.dataset.lineKey,
      quantity,
    });
    renderCartState(dialog, state);
  };

  const swapVariant = async (dialog, variant, quantity) => {
    const cartResponse = await fetch(dialog.dataset.cartUrl, {
      headers: { Accept: 'application/json' },
    });
    const cart = await cartResponse.json();
    if (!cartResponse.ok) throw new Error(dialog.dataset.errorMessage);

    const oldKey = dialog.dataset.lineKey;
    const existingTarget = cart.items.find(
      (cartItem) => Number(cartItem.variant_id) === Number(variant.id) && cartItem.key !== oldKey
    );
    const updates = { [oldKey]: 0 };

    if (existingTarget) {
      updates[existingTarget.key] = existingTarget.quantity + quantity;
    } else {
      updates[String(variant.id)] = quantity;
    }

    const state = await requestCartUpdate(dialog, dialog.dataset.cartUpdateUrl, { updates });
    renderCartState(dialog, state);
  };

  document.addEventListener('click', async (event) => {
    const openButton = event.target.closest('[data-cart-dialog-open]');
    if (openButton) {
      const dialog = document.getElementById(openButton.dataset.cartDialogOpen);
      if (dialog) {
        dialog.querySelector('[data-cart-dialog-error]')?.replaceChildren();
        dialog.showModal();
        if (dialog.matches('[data-cart-edit-dialog]')) refreshEditDialog(dialog);
      }
      return;
    }

    const closeButton = event.target.closest('[data-cart-dialog-close]');
    if (closeButton) {
      closeButton.closest('dialog')?.close();
      return;
    }

    const optionButton = event.target.closest('[data-cart-option-value]');
    if (optionButton) {
      const group = optionButton.closest('[data-cart-option-group]');
      group.querySelectorAll('[data-cart-option-value]').forEach((button) => {
        const selected = button === optionButton;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      group.querySelector('[data-cart-option-selected]').textContent = optionButton.dataset.cartOptionValue;
      refreshEditDialog(optionButton.closest('dialog'));
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity-adjust]');
    if (quantityButton) {
      const dialog = quantityButton.closest('dialog');
      const input = dialog.querySelector('[data-cart-dialog-quantity]');
      const adjustment = Number(quantityButton.dataset.cartQuantityAdjust);
      const minimum = Number(input.min || 1);
      const maximum = Number(input.max || 99);
      input.value = Math.min(maximum, Math.max(minimum, Number(input.value || minimum) + adjustment));
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove-confirm]');
    if (removeButton) {
      const dialog = removeButton.closest('dialog');
      const error = dialog.querySelector('[data-cart-dialog-error]');
      error.textContent = '';
      try {
        setBusy(
          dialog,
          true,
          '[data-cart-remove-confirm]',
          dialog.dataset.removingLabel,
          dialog.dataset.removeLabel
        );
        await changeLineQuantity(dialog, 0);
      } catch (requestError) {
        error.textContent = dialog.dataset.errorMessage;
        setBusy(
          dialog,
          false,
          '[data-cart-remove-confirm]',
          dialog.dataset.removingLabel,
          dialog.dataset.removeLabel
        );
      }
      return;
    }

    const editButton = event.target.closest('[data-cart-edit-confirm]');
    if (editButton) {
      const dialog = editButton.closest('dialog');
      const variant = refreshEditDialog(dialog);
      if (!variant) return;

      const quantityInput = dialog.querySelector('[data-cart-dialog-quantity]');
      const quantity = Math.max(1, Math.min(99, Number(quantityInput.value || 1)));
      const currentVariantId = Number(dialog.dataset.currentVariantId);
      const error = dialog.querySelector('[data-cart-dialog-error]');
      error.textContent = '';

      try {
        setBusy(
          dialog,
          true,
          '[data-cart-edit-confirm]',
          dialog.dataset.updatingLabel,
          dialog.dataset.updateLabel
        );
        if (Number(variant.id) === currentVariantId) {
          await changeLineQuantity(dialog, quantity);
        } else {
          await swapVariant(dialog, variant, quantity);
        }
      } catch (requestError) {
        error.textContent = dialog.dataset.errorMessage;
        setBusy(
          dialog,
          false,
          '[data-cart-edit-confirm]',
          dialog.dataset.updatingLabel,
          dialog.dataset.updateLabel
        );
        refreshEditDialog(dialog);
      }
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target.matches('dialog[data-cart-item-dialog]')) event.target.close();
  });
}
